import React, { useState, useRef, useEffect, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { X, Upload, Download, ImageIcon, Move, ZoomIn, ZoomOut, RotateCcw, History, Users, CheckSquare, Square, Loader2, CheckCircle2, XCircle } from 'lucide-react';

/* ─── localStorage memory ─────────────────────────────────────── */
const LS_KEY = 'qrComposerMemory';

/** FNV-1a 32-bit hash of an ArrayBuffer → hex string */
function fnvHash(buffer) {
  const bytes = new Uint8Array(buffer);
  let h = 0x811c9dc5;
  for (let i = 0; i < bytes.length; i++) {
    h ^= bytes[i];
    h = (Math.imul(h, 0x01000193) >>> 0);
  }
  return h.toString(16).padStart(8, '0');
}

function readMemory() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}'); }
  catch { return {}; }
}

function saveMemory(hash, qrSize, qrPos, label) {
  const mem = readMemory();
  mem[hash] = { qrSize, qrPos, label, savedAt: Date.now() };
  // keep max 20 entries
  const keys = Object.keys(mem);
  if (keys.length > 20) delete mem[keys[0]];
  localStorage.setItem(LS_KEY, JSON.stringify(mem));
}

const SITE_URL = window.location.origin;

/** Render a QR SVG for a given URL into an Image at the given pixel size */
function renderQrToImage(url, size) {
  return new Promise((resolve, reject) => {
    const container = document.createElement('div');
    container.style.cssText = 'position:absolute;left:-9999px;top:-9999px;visibility:hidden';
    document.body.appendChild(container);

    // Use ReactDOM from the same bundle
    import('react-dom/client').then(({ createRoot }) => {
      const root = createRoot(container);
      root.render(React.createElement(QRCodeSVG, { value: url, size: Math.round(size), includeMargin: true, level: 'H' }));
      // Wait for React to flush + paint
      setTimeout(() => {
        const svg = container.querySelector('svg');
        if (!svg) { root.unmount(); container.remove(); reject(new Error('No SVG')); return; }
        svgNodeToImage(svg, size)
          .then(img => { root.unmount(); container.remove(); resolve(img); })
          .catch(err => { root.unmount(); container.remove(); reject(err); });
      }, 100);
    }).catch(reject);
  });
}

/* ─── helpers ─────────────────────────────────────────────────── */

/** Renders the QR SVG node to an HTMLImageElement (Promise) */
function svgNodeToImage(svgEl, size) {
  return new Promise((resolve, reject) => {
    const data = new XMLSerializer().serializeToString(svgEl);
    const url = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(data)));
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

/** Loads a File as an HTMLImageElement */
function fileToImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/* ─── physical size (pHYs) preservation ───────────────────────── */

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

function crc32(bytes) {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/** Extract pixels-per-meter density from a PNG (pHYs) or JPEG (JFIF APP0). Returns {ppuX, ppuY} or null. */
function extractDensity(buffer) {
  const bytes = new Uint8Array(buffer);
  const dv = new DataView(buffer);

  // PNG
  if (bytes.length > 8 && bytes[0]===0x89 && bytes[1]===0x50 && bytes[2]===0x4E && bytes[3]===0x47) {
    let pos = 8;
    while (pos + 12 <= bytes.length) {
      const len = dv.getUint32(pos);
      const type = String.fromCharCode(bytes[pos+4], bytes[pos+5], bytes[pos+6], bytes[pos+7]);
      if (type === 'pHYs' && len >= 9) {
        const ppuX = dv.getUint32(pos + 8);
        const ppuY = dv.getUint32(pos + 12);
        const unit = bytes[pos + 16];
        if (unit === 1) return { ppuX, ppuY };
        return null;
      }
      if (type === 'IDAT' || type === 'IEND') break;
      pos += 12 + len;
    }
    return null;
  }

  // JPEG
  if (bytes.length > 4 && bytes[0]===0xFF && bytes[1]===0xD8) {
    let pos = 2;
    while (pos + 4 <= bytes.length) {
      if (bytes[pos] !== 0xFF) break;
      const marker = bytes[pos + 1];
      if (marker === 0xD9 || marker === 0xDA) break;
      const segLen = dv.getUint16(pos + 2);
      if (marker === 0xE0 && segLen >= 14) {
        const id = String.fromCharCode(bytes[pos+4], bytes[pos+5], bytes[pos+6], bytes[pos+7]);
        if (id === 'JFIF') {
          const unit = bytes[pos + 11]; // 0=aspect, 1=dpi, 2=dpcm
          const xD = dv.getUint16(pos + 12);
          const yD = dv.getUint16(pos + 14);
          if (unit === 1 && xD && yD) {
            return { ppuX: Math.round(xD * 39.3700787), ppuY: Math.round(yD * 39.3700787) };
          }
          if (unit === 2 && xD && yD) {
            return { ppuX: xD * 100, ppuY: yD * 100 };
          }
          return null;
        }
      }
      pos += 2 + segLen;
    }
  }
  return null;
}

/** Insert a pHYs chunk into a PNG ArrayBuffer right after IHDR. */
function injectPhys(pngBuffer, ppuX, ppuY) {
  const bytes = new Uint8Array(pngBuffer);
  const dv = new DataView(pngBuffer);
  const ihdrLen = dv.getUint32(8);
  const insertPos = 8 + 4 + 4 + ihdrLen + 4; // sig + len + 'IHDR' + data + crc

  const chunk = new Uint8Array(4 + 4 + 9 + 4);
  const cdv = new DataView(chunk.buffer);
  cdv.setUint32(0, 9);
  chunk.set([0x70, 0x48, 0x59, 0x73], 4); // 'pHYs'
  cdv.setUint32(8, ppuX);
  cdv.setUint32(12, ppuY);
  chunk[16] = 1; // unit = meters
  cdv.setUint32(17, crc32(chunk.subarray(4, 17)));

  const out = new Uint8Array(bytes.length + chunk.length);
  out.set(bytes.subarray(0, insertPos), 0);
  out.set(chunk, insertPos);
  out.set(bytes.subarray(insertPos), insertPos + chunk.length);
  return out;
}

function canvasToPngBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(b => b ? resolve(b) : reject(new Error('toBlob failed')), 'image/png');
  });
}

async function downloadCanvasWithDensity(canvas, filename, density) {
  const blob = await canvasToPngBlob(canvas);
  let finalBlob = blob;
  if (density && density.ppuX && density.ppuY) {
    const buf = await blob.arrayBuffer();
    const withPhys = injectPhys(buf, density.ppuX, density.ppuY);
    finalBlob = new Blob([withPhys], { type: 'image/png' });
  }
  const url = URL.createObjectURL(finalBlob);
  const a = document.createElement('a');
  a.download = filename;
  a.href = url;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ─── constants ───────────────────────────────────────────────── */
const MIN_QR = 60;
const MAX_QR = 600;
const DEFAULT_QR = 200;
const HANDLE_SIZE = 14; // resize handle size in px

/* ═══════════════════════════════════════════════════════════════ */
/*  QRCodeComposerModal                                            */
/* ═══════════════════════════════════════════════════════════════ */
const QRCodeComposerModal = ({ partner, allPartners = [], onClose }) => {
  const qrUrl = `${SITE_URL}/parceiro/${partner.slug}`;

  /* ── state ── */
  const [campaignImage, setCampaignImage] = useState(null);   // { img, src, w, h, hash }
  const [qrPos, setQrPos] = useState({ x: 20, y: 20 });
  const [qrSize, setQrSize] = useState(DEFAULT_QR);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [restoredMemory, setRestoredMemory] = useState(false); // badge flag

  /* ── batch processing state ── */
  const [batchMode, setBatchMode] = useState(false);
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0, currentName: '', results: [] });

  /* ── refs ── */
  const canvasRef = useRef(null);         // preview canvas
  const svgContainerRef = useRef(null);   // hidden QR SVG
  const fileInputRef = useRef(null);
  const canvasWrapRef = useRef(null);

  /* ─── draw preview ──────────────────────────────────────────── */
  const drawPreview = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas || !campaignImage) return;

    const { img, w, h } = campaignImage;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');

    // background image
    ctx.drawImage(img, 0, 0, w, h);

    // QR code
    const svgEl = svgContainerRef.current?.querySelector('svg');
    if (svgEl) {
      try {
        const qrImg = await svgNodeToImage(svgEl, qrSize);
        ctx.drawImage(qrImg, qrPos.x, qrPos.y, qrSize, qrSize);
      } catch {/* ignore */}
    }
  }, [campaignImage, qrPos, qrSize]);

  useEffect(() => { drawPreview(); }, [drawPreview]);

  /* ─── load campaign image ───────────────────────────────────── */
  const loadCampaignImage = async (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    try {
      // 1. compute hash from raw bytes
      const buffer = await file.arrayBuffer();
      const hash = fnvHash(buffer);
      const density = extractDensity(buffer);

      // 2. load image element
      const img = await fileToImage(file);
      const maxW = 900;
      const scale = img.naturalWidth > maxW ? maxW / img.naturalWidth : 1;
      const w = Math.round(img.naturalWidth * scale);
      const h = Math.round(img.naturalHeight * scale);

      setCampaignImage({ img, src: img.src, w, h, hash, density });

      // 3. restore from memory or use defaults
      const mem = readMemory();
      if (mem[hash]) {
        const saved = mem[hash];
        setQrPos(saved.qrPos);
        setQrSize(saved.qrSize);
        setRestoredMemory(true);
      } else {
        setQrPos({ x: 20, y: 20 });
        setQrSize(DEFAULT_QR);
        setRestoredMemory(false);
      }
    } catch {
      alert('Não foi possível carregar a imagem.');
    }
  };

  /* ─── file input / drop zone ────────────────────────────────── */
  const handleFileChange = (e) => loadCampaignImage(e.target.files?.[0]);
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    loadCampaignImage(e.dataTransfer.files?.[0]);
  };
  const handleDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = () => setIsDragOver(false);

  /* ─── pointer helpers (canvas coords) ──────────────────────── */
  const getCanvasPoint = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const isOnResizeHandle = (pt) => {
    const hx = qrPos.x + qrSize - HANDLE_SIZE;
    const hy = qrPos.y + qrSize - HANDLE_SIZE;
    return pt.x >= hx && pt.x <= hx + HANDLE_SIZE + 4
        && pt.y >= hy && pt.y <= hy + HANDLE_SIZE + 4;
  };

  const isOnQR = (pt) =>
    pt.x >= qrPos.x && pt.x <= qrPos.x + qrSize &&
    pt.y >= qrPos.y && pt.y <= qrPos.y + qrSize;

  /* ─── pointer down ──────────────────────────────────────────── */
  const handlePointerDown = (e) => {
    if (!campaignImage) return;
    e.preventDefault();
    const pt = getCanvasPoint(e);

    if (isOnResizeHandle(pt)) {
      setIsResizing(true);
      setDragStart({ x: pt.x, y: pt.y, size: qrSize });
    } else if (isOnQR(pt)) {
      setIsDragging(true);
      setDragStart({ x: pt.x - qrPos.x, y: pt.y - qrPos.y });
    }
  };

  /* ─── pointer move ──────────────────────────────────────────── */
  const handlePointerMove = useCallback((e) => {
    if (!isDragging && !isResizing) return;
    e.preventDefault();
    const pt = getCanvasPoint(e);

    if (isDragging && dragStart && campaignImage) {
      const { w, h } = campaignImage;
      setQrPos({
        x: Math.max(0, Math.min(pt.x - dragStart.x, w - qrSize)),
        y: Math.max(0, Math.min(pt.y - dragStart.y, h - qrSize)),
      });
    }

    if (isResizing && dragStart && campaignImage) {
      const { w, h } = campaignImage;
      const delta = (pt.x - dragStart.x + pt.y - dragStart.y) / 2;
      const newSize = Math.round(
        Math.max(MIN_QR, Math.min(MAX_QR, dragStart.size + delta))
      );
      // keep within canvas
      setQrSize(Math.min(newSize, w - qrPos.x, h - qrPos.y));
    }
  }, [isDragging, isResizing, dragStart, campaignImage, qrPos, qrSize]);

  /* ─── pointer up ────────────────────────────────────────────── */
  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setDragStart(null);
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handlePointerMove);
    window.addEventListener('mouseup', handlePointerUp);
    window.addEventListener('touchmove', handlePointerMove, { passive: false });
    window.addEventListener('touchend', handlePointerUp);
    return () => {
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('mouseup', handlePointerUp);
      window.removeEventListener('touchmove', handlePointerMove);
      window.removeEventListener('touchend', handlePointerUp);
    };
  }, [handlePointerMove, handlePointerUp]);

  /* ─── cursor style ──────────────────────────────────────────── */
  const handleCanvasMouseMove = (e) => {
    if (!campaignImage) return;
    const pt = getCanvasPoint(e);
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (isOnResizeHandle(pt)) canvas.style.cursor = 'nwse-resize';
    else if (isOnQR(pt))      canvas.style.cursor = 'grab';
    else                       canvas.style.cursor = 'default';
  };

  /* ─── download ──────────────────────────────────────────────── */
  const handleDownload = async () => {
    if (!campaignImage) return;
    const { img, w, hash, density } = campaignImage;

    const origW = img.naturalWidth;
    const origH = img.naturalHeight;
    const scale = origW / w;

    const out = document.createElement('canvas');
    out.width = origW;
    out.height = origH;
    const ctx = out.getContext('2d');
    ctx.drawImage(img, 0, 0, origW, origH);

    const svgEl = svgContainerRef.current?.querySelector('svg');
    if (svgEl) {
      const qrImg = await svgNodeToImage(svgEl, qrSize * scale);
      ctx.drawImage(qrImg, qrPos.x * scale, qrPos.y * scale, qrSize * scale, qrSize * scale);
    }

    await downloadCanvasWithDensity(out, `campanha-qr-${partner.slug}.png`, density);

    // 💾 persist settings for this image
    if (hash) {
      saveMemory(hash, qrSize, qrPos, partner.name);
      setRestoredMemory(false); // already up-to-date, hide "restored" badge
    }
  };

  /* ─── size slider ───────────────────────────────────────────── */
  const clampQrSize = (newSize) => {
    if (!campaignImage) return;
    const clamped = Math.max(MIN_QR, Math.min(MAX_QR, newSize,
      campaignImage.w - qrPos.x, campaignImage.h - qrPos.y));
    setQrSize(clamped);
  };

  /* ─── reset position ────────────────────────────────────────── */
  const resetQr = () => {
    setQrPos({ x: 20, y: 20 });
    setQrSize(DEFAULT_QR);
    setRestoredMemory(false);
  };

  /* ─── batch processing ─────────────────────────────────────── */
  const activePartners = allPartners.filter(p => p.active);

  const togglePartner = (id) => {
    setSelectedPartners(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    if (selectedPartners.length === activePartners.length) {
      setSelectedPartners([]);
    } else {
      setSelectedPartners(activePartners.map(p => p.id));
    }
  };

  const sleep = (ms) => new Promise(r => setTimeout(r, ms));

  const runBatch = async () => {
    if (!campaignImage || selectedPartners.length === 0) return;
    setBatchRunning(true);
    const chosen = activePartners.filter(p => selectedPartners.includes(p.id));
    const results = [];
    setBatchProgress({ current: 0, total: chosen.length, currentName: '', results });

    const { img, w, density } = campaignImage;
    const origW = img.naturalWidth;
    const origH = img.naturalHeight;
    const scale = origW / w;

    for (let i = 0; i < chosen.length; i++) {
      const p = chosen[i];
      setBatchProgress(prev => ({ ...prev, current: i + 1, currentName: p.name }));
      try {
        const pUrl = `${SITE_URL}/parceiro/${p.slug}`;
        const out = document.createElement('canvas');
        out.width = origW;
        out.height = origH;
        const ctx = out.getContext('2d');
        ctx.drawImage(img, 0, 0, origW, origH);

        const qrImg = await renderQrToImage(pUrl, qrSize * scale);
        ctx.drawImage(qrImg, qrPos.x * scale, qrPos.y * scale, qrSize * scale, qrSize * scale);

        await downloadCanvasWithDensity(out, `campanha-qr-${p.slug}.png`, density);

        results.push({ name: p.name, ok: true });
        await sleep(600); // small delay between downloads
      } catch {
        results.push({ name: p.name, ok: false });
      }
      setBatchProgress(prev => ({ ...prev, results: [...results] }));
    }

    // save memory for current image
    if (campaignImage.hash) {
      saveMemory(campaignImage.hash, qrSize, qrPos, 'batch');
    }
    setBatchRunning(false);
  };

  /* ════════════════════════════════════════════════════════════ */
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[60] overflow-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl flex flex-col max-h-[95vh]">

        {/* ── header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-800">Compositor de QR Code</h2>
            <p className="text-xs text-gray-500 mt-0.5">Parceiro: <span className="font-medium text-orange-600">{partner.name}</span></p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
            title="Fechar"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* ── body ── */}
        <div className="flex flex-col lg:flex-row flex-1 overflow-hidden gap-0">

          {/* LEFT — controls */}
          <div className="lg:w-64 p-5 border-b lg:border-b-0 lg:border-r border-gray-100 flex flex-col gap-4 shrink-0">

            {/* Upload zone */}
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">1. Imagem de Campanha</p>
              <div
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all select-none ${
                  isDragOver
                    ? 'border-orange-400 bg-orange-50'
                    : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50/40'
                }`}
              >
                <ImageIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-xs text-gray-500">
                  {campaignImage ? 'Trocar imagem' : 'Clique ou arraste uma imagem'}
                </p>
                <p className="text-[10px] text-gray-400 mt-1">PNG, JPG, WEBP…</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
              {/* Memory restored badge */}
              {restoredMemory && (
                <div className="mt-2 flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5">
                  <History className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <p className="text-[11px] text-emerald-700 font-medium leading-tight">
                    Posição e tamanho restaurados da última vez que usou esta imagem
                  </p>
                </div>
              )}
            </div>

            {/* QR size */}
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">2. Tamanho do QR Code</p>
              <div className="flex items-center gap-2">
                <ZoomOut className="w-4 h-4 text-gray-400 shrink-0" />
                <input
                  type="range"
                  min={MIN_QR}
                  max={campaignImage ? Math.min(MAX_QR, campaignImage.w - qrPos.x, campaignImage.h - qrPos.y) : MAX_QR}
                  value={qrSize}
                  onChange={(e) => clampQrSize(Number(e.target.value))}
                  className="flex-1 accent-orange-500"
                  disabled={!campaignImage}
                />
                <ZoomIn className="w-4 h-4 text-gray-400 shrink-0" />
              </div>
              <p className="text-[11px] text-gray-400 text-center mt-1">{qrSize}px</p>
            </div>

            {/* Instructions */}
            <div className="bg-orange-50 rounded-xl p-3">
              <p className="text-xs font-semibold text-orange-700 mb-1.5">Como usar</p>
              <ul className="text-[11px] text-orange-600 space-y-1">
                <li className="flex gap-1.5"><Move className="w-3 h-3 mt-0.5 shrink-0" /><span>Arraste o QR Code para reposicioná-lo</span></li>
                <li className="flex gap-1.5"><ZoomIn className="w-3 h-3 mt-0.5 shrink-0" /><span>Use o controle abaixo para redimensionar</span></li>
                <li className="flex gap-1.5"><span className="text-orange-400 font-bold shrink-0">⊡</span><span>Ou arraste o canto inferior-direito do QR</span></li>
              </ul>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col gap-2 mt-auto">
              <button
                onClick={resetQr}
                disabled={!campaignImage}
                className="flex items-center justify-center gap-2 w-full py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Resetar posição
              </button>
              <button
                onClick={handleDownload}
                disabled={!campaignImage}
                className="flex items-center justify-center gap-2 w-full py-2.5 text-sm font-semibold text-white bg-orange-600 hover:bg-orange-700 rounded-xl disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow"
              >
                <Download className="w-4 h-4" />
                Baixar imagem
              </button>
              {activePartners.length > 0 && (
                <button
                  onClick={() => setBatchMode(!batchMode)}
                  disabled={!campaignImage}
                  className={`flex items-center justify-center gap-2 w-full py-2 text-sm font-medium rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                    batchMode
                      ? 'bg-purple-100 text-purple-700 border border-purple-300'
                      : 'text-purple-600 border border-purple-200 hover:bg-purple-50'
                  }`}
                >
                  <Users className="w-4 h-4" />
                  Batch Processing
                </button>
              )}
            </div>
          </div>

          {/* RIGHT — canvas preview + batch panel */}
          <div className="flex-1 overflow-auto bg-[#f0f0f0] bg-[repeating-conic-gradient(#ccc_0%_25%,transparent_0%_50%)] bg-[length:20px_20px] flex flex-col">
            <div className="flex-1 flex items-center justify-center p-4">
              {!campaignImage ? (
                <div className="text-center pointer-events-none select-none">
                  <Upload className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-400 text-sm font-medium">Adicione uma imagem de campanha</p>
                  <p className="text-gray-300 text-xs mt-1">O QR Code aparecerá sobre ela aqui</p>
                </div>
              ) : (
                <div
                  ref={canvasWrapRef}
                  className="relative"
                  style={{ lineHeight: 0 }}
                >
                  <canvas
                    ref={canvasRef}
                    onMouseDown={handlePointerDown}
                    onTouchStart={handlePointerDown}
                    onMouseMove={handleCanvasMouseMove}
                    style={{
                      maxWidth: '100%',
                      maxHeight: batchMode ? '50vh' : '70vh',
                      display: 'block',
                      borderRadius: '8px',
                      boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
                      userSelect: 'none',
                      touchAction: 'none',
                    }}
                  />
                </div>
              )}
            </div>

            {/* ── batch panel ── */}
            {batchMode && campaignImage && (
              <div className="border-t border-gray-200 bg-white/95 backdrop-blur p-4 max-h-[40vh] overflow-auto">
                {!batchRunning && batchProgress.results.length === 0 && (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                        <Users className="w-4 h-4 text-purple-500" />
                        Selecionar Parceiros
                      </h3>
                      <button
                        onClick={toggleAll}
                        className="text-xs font-medium text-purple-600 hover:text-purple-800 transition-colors"
                      >
                        {selectedPartners.length === activePartners.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mb-4">
                      {activePartners.map(p => (
                        <label
                          key={p.id}
                          className={`flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer transition-all text-sm ${
                            selectedPartners.includes(p.id)
                              ? 'bg-purple-50 border border-purple-200 text-purple-800'
                              : 'bg-gray-50 border border-transparent hover:bg-gray-100 text-gray-600'
                          }`}
                        >
                          {selectedPartners.includes(p.id)
                            ? <CheckSquare className="w-4 h-4 text-purple-500 shrink-0" />
                            : <Square className="w-4 h-4 text-gray-300 shrink-0" />
                          }
                          <span className="truncate font-medium">{p.name}</span>
                          <span className="text-[10px] text-gray-400 ml-auto shrink-0">{p.type}</span>
                          <input
                            type="checkbox"
                            className="hidden"
                            checked={selectedPartners.includes(p.id)}
                            onChange={() => togglePartner(p.id)}
                          />
                        </label>
                      ))}
                    </div>
                    <button
                      onClick={runBatch}
                      disabled={selectedPartners.length === 0}
                      className="flex items-center justify-center gap-2 w-full py-2.5 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-xl disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow"
                    >
                      <Download className="w-4 h-4" />
                      Gerar {selectedPartners.length} Arte{selectedPartners.length !== 1 ? 's' : ''}
                    </button>
                  </>
                )}

                {/* Progress */}
                {batchRunning && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Loader2 className="w-5 h-5 text-purple-500 animate-spin shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-700">
                          Gerando {batchProgress.current} de {batchProgress.total}…
                        </p>
                        <p className="text-xs text-gray-500 truncate">{batchProgress.currentName}</p>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(batchProgress.current / batchProgress.total) * 100}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Results */}
                {!batchRunning && batchProgress.results.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Batch Concluído — {batchProgress.results.filter(r => r.ok).length}/{batchProgress.results.length} sucesso
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-32 overflow-auto">
                      {batchProgress.results.map((r, i) => (
                        <div key={i} className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded ${
                          r.ok ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'
                        }`}>
                          {r.ok ? <CheckCircle2 className="w-3 h-3 shrink-0" /> : <XCircle className="w-3 h-3 shrink-0" />}
                          <span className="truncate">{r.name}</span>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => { setBatchProgress({ current: 0, total: 0, currentName: '', results: [] }); setSelectedPartners([]); }}
                      className="w-full py-2 text-sm font-medium text-purple-600 border border-purple-200 rounded-xl hover:bg-purple-50 transition-colors"
                    >
                      Novo Batch
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hidden QR SVG used for rendering */}
      <div ref={svgContainerRef} style={{ position: 'absolute', left: -9999, top: -9999, visibility: 'hidden' }}>
        <QRCodeSVG value={qrUrl} size={qrSize} includeMargin level="H" />
      </div>
    </div>
  );
};

export default QRCodeComposerModal;
